import { notify, useModal } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useMarkAccountStepDone } from '@features/quickStart/hooks/useMarkAccountStepDone'
import { mapCompanyFormToAccountInfo } from '@features/quickStart/utils'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { ModalId } from '../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { useForm, UseFormReturn } from '../../../../../hooks'
import { isCompanyNameDefault } from '../../../../../utils'
import { useUpdateCompanyInformation } from '../../../hooks/useUpdateCompanyInformation'
import { useVatConfirmationWarningModal } from '../../../hooks/useVatConfirmationWarningModal'
import { CompanyInformationForm } from '../../../types/companyInformationForm'
import { usePredefinedCompanyInformationValues } from '../hooks/usePredefinedCompanyInformationValues'
import { CompanyInformationFormSchema } from '../types/companyInformationFormSchema'
import { getCompanyInformationFormSchema } from '../utils/getCompanyInformationFormSchema'
import { getDefaultCompanyInformationValues } from '../utils/getDefaultCompanyInformationValues'

interface CompanyInformationFormState {
  closeModal: () => void
  companyInformationForm: UseFormReturn<CompanyInformationFormSchema>
  countryId?: string
  isLoading: boolean
  isProcessing: boolean
  submitForm: () => void
}

interface CompanyInformationFormProviderProps {
  children: ReactNode
  onSubmit?: () => void
}

const CompanyInformationFormContext = createContext<CompanyInformationFormState | undefined>(undefined)

export const CompanyInformationFormProvider = ({
  children,
  onSubmit,
}: CompanyInformationFormProviderProps): ReactElement => {
  const { t } = useTranslation()
  const { close: closeModal } = useModal(ModalId.CompanyInformationModal)

  const { organization, isLoading: isLoadingUserOrganization } = useUserOrganization()
  const { user } = useCurrentUser()
  const { predefinedCompanyInformationFormValues, isLoading: isLoadingPredefinedCompanyInformationValues } =
    usePredefinedCompanyInformationValues()

  const markAccountStepDone = useMarkAccountStepDone()

  const { update: updateCompanyInformation, isProcessing } = useUpdateCompanyInformation({
    onError: () => {
      notify({
        id: NotificationKeys.CompanyInformationUpdated,
        message: t('external_invoices.company_information_modal.updated_error'),
        variant: 'error',
      })
    },
    onSuccess: (formData) => {
      notify({
        id: NotificationKeys.CompanyInformationUpdated,
        message: t('external_invoices.company_information_modal.updated_success'),
        variant: 'success',
      })
      onSubmit?.()
      closeModal()

      const organizationAccountInfo = mapCompanyFormToAccountInfo(formData, organization?.countryId)

      markAccountStepDone(organizationAccountInfo)
    },
  })
  const { saveWithWarning, modals: warningModals } = useVatConfirmationWarningModal()

  const isLoading = isLoadingUserOrganization || isLoadingPredefinedCompanyInformationValues

  const companyInformationForm = useForm<CompanyInformationFormSchema>({
    defaultValues: getDefaultCompanyInformationValues(),
    validationSchema: getCompanyInformationFormSchema(t, organization?.countryId),
  })

  const { Form, handleSubmit, reset: resetForm } = companyInformationForm

  const handleFormSubmit = useCallback(
    (values: CompanyInformationForm) => {
      if (!organization) {
        return
      }
      saveWithWarning({
        values,
        valuesToCompare: {
          isVatExempted: organization.isVatExempted,
        },
        onWarningsConfirm: () => updateCompanyInformation(values),
      })
    },
    [organization, saveWithWarning, updateCompanyInformation],
  )

  const submitForm = useCallback(() => {
    handleSubmit(handleFormSubmit)()
  }, [handleSubmit, handleFormSubmit])

  const contextValue = useMemo(
    () => ({
      closeModal,
      companyInformationForm,
      countryId: organization?.countryId,
      isLoading,
      isProcessing,
      submitForm,
    }),
    [closeModal, companyInformationForm, organization?.countryId, isLoading, isProcessing, submitForm],
  )

  useEffect(() => {
    if (isLoading || !organization?.name || !predefinedCompanyInformationFormValues || !user?.email) {
      return
    }
    const isDefaultCompanyName = isCompanyNameDefault({ companyName: organization.name, userEmail: user.email })
    const updatedFormValues = getDefaultCompanyInformationValues({
      predefinedValues: predefinedCompanyInformationFormValues,
      isDefaultCompanyName,
      contactEmail: user.email,
    })

    resetForm(updatedFormValues)
  }, [isLoading, organization?.name, predefinedCompanyInformationFormValues, resetForm, user?.email])

  return (
    <CompanyInformationFormContext.Provider value={contextValue}>
      <Form>{children}</Form>
      {warningModals}
    </CompanyInformationFormContext.Provider>
  )
}

export const useCompanyInformationForm = () => {
  const context = useContext(CompanyInformationFormContext)
  if (!context) {
    throw new Error('CompanyInformationFormContext missing!')
  }
  return context
}
